import React, { useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { Text } from '../../components';
import moment from 'moment';

import { useUserContext } from '../../../common/utils/context';
import { useConfirmStockLevelsContext } from '../../utils/context';
import { Button, ErrorCallout, WarningCallout } from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getExpiryDatesBlockers, shouldShowExpiryBeforeNextServiceWarning } from '../../../common/utils/serviceDocket/helpers';

const useExpiryDatesBlockers = (expiryDatesArray, tenantSettings, referenceDate, timezone) => {

	return useMemo(() => {
		const {
			expiredProductsCount,
			expireTodayCount,
			productsPlacedInIncorrectOrder,
			hasExpiryDatesNotFilledCount,
			productsExpiringWithinXDaysCount,
		} = getExpiryDatesBlockers(expiryDatesArray, tenantSettings, {
			referenceDate,
			timezone,
		});

		return {
			expiredProductsCount,
			expireTodayCount,
			productsPlacedInIncorrectOrder,
			hasExpiryDatesNotFilledCount,
			productsExpiringWithinXDaysCount,
		};
	}, [expiryDatesArray, referenceDate, tenantSettings, timezone]);
};

const useExpiryDatesWanings = ({ expiredProductsCount, expireTodayCount, hasExpiryDatesNotFilledCount, productsPlacedInIncorrectOrder, productsExpiringWithinXDaysCount, expiryDatesAutoSorted, tenantSettings, isMarket, productInSchedule, numberOfRecommendedProductsToRemove, product }) => {

	return useMemo(() => {
		const {
			showExpiryDateWarning,
			mandatoryWarningText,
			optionalWarningText,
		} = getExpiryDatesWarning({
			expiredProductsCount,
			expireTodayCount,
			hasExpiryDatesNotFilledCount,
			productsPlacedInIncorrectOrder,
			productsExpiringWithinXDaysCount,
			expiryDatesAutoSorted,
			tenantSettings,
			isMarket,
			productInSchedule,
			numberOfRecommendedProductsToRemove: shouldShowExpiryBeforeNextServiceWarning({ tenantSettings, product }) ? numberOfRecommendedProductsToRemove : 0,
		});

		return {
			showExpiryDateWarning,
			mandatoryWarningText,
			optionalWarningText,
		};

	}, [expireTodayCount, expiredProductsCount, hasExpiryDatesNotFilledCount, isMarket, productsExpiringWithinXDaysCount, productsPlacedInIncorrectOrder, expiryDatesAutoSorted, tenantSettings, productInSchedule, numberOfRecommendedProductsToRemove, product]);
};


// showExpiryDateButtons is a setting that will show the expiry date buttons even if the product is not in the schedule
const ExpiryDateButtons = ({ planogramItem, showAdvancedView, forceShowButtons, isDialog, showExpiryDateButtons }) => {

	const { tenantSettings } = useUserContext();
	const styles = useThemedStyles(createStyles);
	const {
		changeStockItem,
		expiryReferenceDate,
		isMarket,
		onClickExpiryDateButton,
		timezone,
	} = useConfirmStockLevelsContext();

	const {
		existingExpiryDates,
		expiryDates: expiryDatesObj,
		expiryDatesAutoSorted,
		productInSchedule, // product is in schedule
		product,
		presentProductQuantity,
		numberOfRecommendedProductsToRemove, // how many products could be removed until next service
	} = planogramItem;

	const expiryDatesArray = useMemo(() => {
		if (isMarket) {
			return [expiryDatesObj?.[1]];
		}

		return [...Array(presentProductQuantity)].map((_, i) => expiryDatesObj?.[i + 1] || null);
	}, [isMarket, presentProductQuantity, expiryDatesObj]);

	const {
		expiredProductsCount,
		expireTodayCount,
		productsPlacedInIncorrectOrder,
		hasExpiryDatesNotFilledCount,
		productsExpiringWithinXDaysCount,
	} = useExpiryDatesBlockers(expiryDatesArray, tenantSettings, expiryReferenceDate, timezone);

	const {
		showExpiryDateWarning,
		mandatoryWarningText,
		optionalWarningText,
	} = useExpiryDatesWanings({
		expiredProductsCount,
		expireTodayCount,
		hasExpiryDatesNotFilledCount,
		productsPlacedInIncorrectOrder,
		productsExpiringWithinXDaysCount,
		expiryDatesAutoSorted,
		tenantSettings,
		isMarket,
		productInSchedule,
		numberOfRecommendedProductsToRemove,
		product,
	});

	const { forceFillDates, showButtons } = useMemo(() => {

		if (!productInSchedule && !showExpiryDateWarning && !forceShowButtons && !showExpiryDateButtons) {
			return { forceFillDates: false, showButtons: false };
		}

		const forceFillDates = expireTodayCount || expiredProductsCount || productsPlacedInIncorrectOrder;
		const showButtons = showExpiryDateButtons ||
			productInSchedule ||
			forceShowButtons ||
			forceFillDates ||
			productsExpiringWithinXDaysCount ||
			showExpiryDateWarning;

		return { forceFillDates, showButtons };
	}, [showExpiryDateWarning, forceShowButtons, showExpiryDateButtons, expireTodayCount, productInSchedule, expiredProductsCount, productsPlacedInIncorrectOrder, productsExpiringWithinXDaysCount]);

	if ((!presentProductQuantity && (!isMarket || !expiryDatesObj?.[1])) || !showButtons) {
		return null;
	}

	return (
		<View
			style={[
				styles.expiryDateContainer,
				{ width: '100%', justifyContent: 'space-around', marginTop: showAdvancedView ? 1 : 5 },
			]}
		>
			{ showButtons ? (
				<View>
					<View style={[styles.warningContainer, styles.flexRowButtons, styles.warningTextContainer, optionalWarningText && { paddingBottom: 0 }]}>
						{
							showExpiryDateWarning && mandatoryWarningText ? (
								<View style={[styles.textContainer]}>
									<ErrorCallout
										text={mandatoryWarningText}
									/>
								</View>
							) : null
						}
					</View>
					<View style={[styles.warningContainer, styles.flexRowButtons, styles.warningTextContainer]}>
						{
							showExpiryDateWarning && optionalWarningText ? (
								<View style={[styles.textContainer]}>
									<WarningCallout
										text={optionalWarningText}
									/>
								</View>
							) : null
						}
					</View>
				</View>
			) : null}

			{/* show expiry buttons */}
			<View style={[styles.flexRowButtons, { marginBottom: 5 }]}>
				<View style={styles.edgeLabelStart}><Text style={styles.edgeLabel}>{isMarket ? 'Next product expiry date' : 'Front'}</Text></View>
				<ScrollView
					horizontal={true}
					contentContainerStyle={[
						styles.flexRowButtons,
						(presentProductQuantity <= 3 && !isMarket ? { width: '100%', justifyContent: 'space-around' } : {}),
					]}
					keyboardShouldPersistTaps="handled"
				>
					{
						// create expiryButtons
						expiryDatesArray.map((value, i) => {
							return (
								<Button
									style={[forceFillDates ? styles.expiryButtonRed : styles.expiryButton]}
									labelStyle={forceFillDates ? [styles.labelStyle] : []}
									onPress={onClickExpiryDateButton && onClickExpiryDateButton(planogramItem.id, (i + 1).toString(), value ? moment(value) : moment())}
									key={`${i + 1}`}
									label={value ? moment(value).format('MMM DD') : 'Unknown'}
								/>
							);
						})
					}
					{
						// create applyPreviousButton
						isMarket && !isDialog && existingExpiryDates?.[1] && !expiryDatesObj?.[1] ? (
							<View style={[styles.flexRowButtons, { marginLeft: 15, maxWidth: '50%' }]}>
								<Button
									style={[styles.applyPreviousButton]}
									labelStyle={[styles.previousButtonLabel]}
									onPress={() => changeStockItem(planogramItem.id, 'expiryDates')({ date: existingExpiryDates?.[1], selection: 1 })}
									label={`Apply previous date: ${moment(existingExpiryDates?.[1]).format('MMM DD')}`}
								/>
							</View>

						) : null
					}

				</ScrollView>
				{
					// show user where is back of the machine
					!isMarket ? (
						<View style={styles.edgeLabelEnd}>
							<Text style={styles.edgeLabel}>Back</Text>
						</View>
					) : null
				}

			</View>
		</View>
	);
};



const createStyles = ({ ui: { colors, type, space } }) => ({
	expiryButton: {
		marginRight: 6,
	},
	expiryButtonRed: {
		marginRight: 6,
		borderColor: colors.danger,
	},
	applyPreviousButton: {
		borderColor: colors.currentBorder,
	},
	previousButtonLabel: {
		color: colors.accent,
	},
	labelStyle: {
		color: colors.danger,
	},
	flexRowButtons: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	expiryDateContainer: {
		marginLeft: 'auto',
	},
	warningContainer: {
		alignItems: 'center',
		paddingBottom: space.xs,
	},
	warningTextContainer: {
		marginTop: space.xs,
	},
	textContainer: {
		flex: 1,
		minWidth: '96%',
	},
	edgeLabelStart: {
		marginRight: space.xs,
		maxWidth: 100,
	},
	edgeLabelEnd: {
		marginLeft: space.xs,
	},
	edgeLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
});

const getExpiryDatesWarning = ({
	expiredProductsCount,
	expireTodayCount,
	hasExpiryDatesNotFilledCount,
	productsPlacedInIncorrectOrder,
	productsExpiringWithinXDaysCount,
	expiryDatesAutoSorted,
	tenantSettings,
	isMarket,
	productInSchedule,
	numberOfRecommendedProductsToRemove,
}) => {

	const mandatoryWarningTextArray = [];
	const optionalWarningTextArray = [];

	if (expiredProductsCount) {
		mandatoryWarningTextArray.push(`${!isMarket ? `${expiredProductsCount} product(s) expired` : 'Some products expired'}`);
	}

	if (expireTodayCount) {
		mandatoryWarningTextArray.push(`${!isMarket ? `${expireTodayCount} product(s) expire today` : 'Some products expire today'}`);
	}

	if (productsPlacedInIncorrectOrder && !isMarket) {
		mandatoryWarningTextArray.push(`${!isMarket ? `${productsPlacedInIncorrectOrder} product(s) placed in incorrect order` : 'Some products placed in incorrect order'}`);
	}

	if (productsExpiringWithinXDaysCount) {
		optionalWarningTextArray.push(`${!isMarket ? `${productsExpiringWithinXDaysCount} product(s) expire within ${tenantSettings.expiryDateThresholds[0]} days` : `Some products expire within ${tenantSettings.expiryDateThresholds[0]} days`}`);
	}

	if (hasExpiryDatesNotFilledCount && productInSchedule) {
		optionalWarningTextArray.push(`${!isMarket ? `${hasExpiryDatesNotFilledCount} product(s) have unknown expiry` : 'Some products have unknown expiry'}`);
	}

	if (numberOfRecommendedProductsToRemove) {
		optionalWarningTextArray.push(`${!isMarket ? `${numberOfRecommendedProductsToRemove} product(s) may expire before the next run` : 'Some products may expire before the next run'}`);
	}

	if (expiryDatesAutoSorted && !isMarket) {
		optionalWarningTextArray.push('Expiry dates were sorted. Move products so the earliest dates are at the front.');
	}

	return {
		showExpiryDateWarning: [
			productsExpiringWithinXDaysCount,
			(hasExpiryDatesNotFilledCount && productInSchedule),
			expireTodayCount,
			expiredProductsCount,
			productsPlacedInIncorrectOrder,
			expiryDatesAutoSorted,
			numberOfRecommendedProductsToRemove,
		].some(i => Boolean(i)),
		warningCount: mandatoryWarningTextArray.length,
		mandatoryWarningText: mandatoryWarningTextArray.length > 0 ? mandatoryWarningTextArray.join('\n') : null,
		optionalWarningText: optionalWarningTextArray.length > 0 ? optionalWarningTextArray.join('\n') : null,
	};
};

export default React.memo(ExpiryDateButtons);
